'use client'

import { createContext, type ReactNode, useContext, useState } from 'react'

export type AuditedEmail = { html: string }

type AuditedEmailState = {
  audited: AuditedEmail | null
  setAudited: (next: AuditedEmail | null) => void
}

const AuditedEmailContext = createContext<AuditedEmailState | null>(null)

/**
 * The email this `/email-audit` page last audited, in memory only. The audit
 * island writes it; the rendering band further down reads it, so its button
 * can carry that email into Brew. Scoped to one page: leaving the page drops
 * it, and a shared report renders no provider, so its band never carries an
 * email it did not show. Renders no element of its own.
 */
export function AuditedEmailProvider({ children }: { children: ReactNode }) {
  const [audited, setAudited] = useState<AuditedEmail | null>(null)
  return (
    <AuditedEmailContext value={{ audited, setAudited }}>
      {children}
    </AuditedEmailContext>
  )
}

/** The page's audited email; null before an audit and outside the provider. */
export function useAuditedEmail(): AuditedEmail | null {
  return useContext(AuditedEmailContext)?.audited ?? null
}

const IGNORE = () => {}

/** Records (or clears) the page's audited email; a no-op outside the provider. */
export function useSetAuditedEmail(): AuditedEmailState['setAudited'] {
  return useContext(AuditedEmailContext)?.setAudited ?? IGNORE
}
